// src/data/applicationsStore.ts

export interface SubmittedApplication {
  id: string; // e.g. MYS-IND-84920
  destination: string; // e.g. INDIA, MALAYSIA, VIETNAM
  countryName: string;
  applicantName: string;
  passportNumber: string;
  nationality: string;
  visaCategory: string;
  status: 'Pending 🟡' | 'Processing 🔵' | 'Approved 🟢' | 'Action Required 🔴';
  submittedAt: string;
  totalFeeLkr: number;
  data: Record<string, any>;
  photoBase64?: string;
  passportBase64?: string;
}

const STORAGE_KEY = 'mySkySubmittedApps';

export const loadSubmittedApplications = (): SubmittedApplication[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (err) {
    console.error('Failed to load submitted applications:', err);
  }
  return [];
};

export const saveSubmittedApplication = (app: SubmittedApplication) => {
  try {
    const apps = loadSubmittedApplications();
    const existingIndex = apps.findIndex((a) => a.id === app.id);
    if (existingIndex >= 0) {
      apps[existingIndex] = app;
    } else {
      apps.unshift(app);
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
    } catch (quotaErr) {
      console.warn('localStorage full, stripping base64 images to save submitted application metadata:', quotaErr);
      // Strip large image payloads if quota is exceeded
      const lightweightApps = apps.map((a) => {
        const copy = { ...a };
        delete copy.photoBase64;
        delete copy.passportBase64;
        return copy;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lightweightApps));
    }

    window.dispatchEvent(new Event('submittedAppsUpdated'));
  } catch (err) {
    console.error('Failed to save submitted application:', err);
  }
};

export const updateApplicationStatus = (id: string, status: SubmittedApplication['status']) => {
  try {
    const apps = loadSubmittedApplications();
    const target = apps.find((a) => a.id === id);
    if (target) {
      target.status = status;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
      window.dispatchEvent(new Event('submittedAppsUpdated'));
    }
  } catch (err) {
    console.error('Failed to update application status:', err);
  }
};

export const deleteSubmittedApplication = (id: string) => {
  try {
    const apps = loadSubmittedApplications().filter((a) => a.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
    window.dispatchEvent(new Event('submittedAppsUpdated'));
  } catch (err) {
    console.error('Failed to delete submitted application:', err);
  }
};

export const exportApplicationsToCSV = (apps: SubmittedApplication[]) => {
  if (!apps.length) {
    alert('No application records to export!');
    return;
  }

  const headers = [
    'Reference ID',
    'Destination',
    'Applicant Name',
    'Passport Number',
    'Nationality',
    'Visa Category',
    'Status',
    'Fee (LKR)',
    'Submission Date',
  ];

  const rows = apps.map((a) => [
    `"${a.id}"`,
    `"${a.countryName}"`,
    `"${a.applicantName.replace(/"/g, '""')}"`,
    `"${a.passportNumber}"`,
    `"${a.nationality}"`,
    `"${a.visaCategory.replace(/"/g, '""')}"`,
    `"${a.status}"`,
    a.totalFeeLkr,
    `"${a.submittedAt}"`,
  ]);

  const csvText = [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csvText], { type: 'text/csv;charset=utf-8;' });
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', `mySky_e-Visa_Submissions_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

